import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Loader2, RefreshCw, AlertTriangle, CheckCircle, Search,
  Calculator, ArrowRight, Users, Database, ShieldCheck
} from 'lucide-react';
import { BackButton } from '../../components/common';

export const AdminDataAuditPage = () => {
  const navigate = useNavigate();
  
  // Discrepancy state
  const [discrepancies, setDiscrepancies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanned, setScanned] = useState(false);
  const [summary, setSummary] = useState({
    total_users_affected: 0,
    total_discrepancy_millis: 0
  });
  
  // Selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Apply fixes state
  const [applyLoading, setApplyLoading] = useState(false);
  const [fixResults, setFixResults] = useState(null);

  const fetchDiscrepancies = useCallback(async () => {
    setLoading(true);
    setScanned(false);
    setFixResults(null);
    try {
      const response = await adminAPI.getBalanceDiscrepancies();
      const data = response.data;
      setDiscrepancies(data.discrepancies || []);
      setSummary({
        total_users_affected: data.total_users_affected || 0,
        total_discrepancy_millis: data.total_discrepancy_millis || 0
      });
      setScanned(true);
      setSelectedUsers([]);
      setSelectAll(false);
      
      if (data.discrepancies?.length === 0) {
        toast.success('All user balances are correct!');
      } else {
        toast.info(`Found ${data.discrepancies.length} users with balance discrepancies`);
      }
    } catch (error) {
      console.error('Fetch error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/home-new');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to scan for discrepancies');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedUsers(discrepancies.map(d => d.user_id));
    } else {
      setSelectedUsers([]);
    }
  };

  const handleSelectUser = (userId, checked) => {
    if (checked) {
      setSelectedUsers(prev => [...prev, userId]);
    } else {
      setSelectedUsers(prev => prev.filter(id => id !== userId));
      setSelectAll(false);
    }
  };

  const handleApplyFixes = async () => {
    if (selectedUsers.length === 0) {
      toast.error('Please select at least one user to fix');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to recalculate and fix balances for ${selectedUsers.length} user(s)?\n\nThis action will update the stored milli balance to match the calculated balance from transactions.`
    );

    if (!confirmed) return;

    setApplyLoading(true);
    try {
      const response = await adminAPI.applyBalanceFixes(selectedUsers);
      const data = response.data;
      
      setFixResults(data);
      toast.success(`Successfully fixed ${data.fixes_applied} user balance(s)`);
      
      // Refresh the discrepancies list
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Apply fixes error:', error);
      toast.error(error.response?.data?.detail || 'Failed to apply fixes');
    } finally {
      setApplyLoading(false);
    }
  };

  const handleFixAll = async () => {
    if (discrepancies.length === 0) {
      toast.error('No discrepancies to fix');
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to fix ALL ${discrepancies.length} user(s) with balance discrepancies?\n\nThis will recalculate and update all affected balances.`
    );

    if (!confirmed) return;

    setApplyLoading(true);
    try {
      const allUserIds = discrepancies.map(d => d.user_id);
      const response = await adminAPI.applyBalanceFixes(allUserIds);
      const data = response.data;
      
      setFixResults(data);
      toast.success(`Successfully fixed ${data.fixes_applied} user balance(s)`);
      
      // Refresh
      await fetchDiscrepancies();
    } catch (error) {
      console.error('Fix all error:', error);
      toast.error(error.response?.data?.detail || 'Failed to fix all balances');
    } finally {
      setApplyLoading(false);
    }
  };

  const formatMilli = (value) => {
    if (value === null || value === undefined) return '-';
    return value.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white p-4 sm:p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <BackButton />
            <div>
              <h1 className="text-2xl font-bold text-amber-900">Data Audit</h1>
              <p className="text-sm text-muted-foreground">Find and fix user balance discrepancies</p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Database className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-lg font-bold text-blue-700">
                    {scanned ? 'Scan Complete' : 'Not Scanned'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Users Affected</p>
                  <p className="text-lg font-bold text-amber-700">
                    {summary.total_users_affected}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Discrepancy</p>
                  <p className="text-lg font-bold text-red-700">
                    {formatMilli(summary.total_discrepancy_millis)} Millis
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Scan Button */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Scan for Balance Discrepancies
            </CardTitle>
            <CardDescription>
              Compare stored user balances with calculated balances from all transactions including admin adjustments
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button 
              onClick={fetchDiscrepancies} 
              disabled={loading}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="scan-discrepancies-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Scanning All Users...
                </>
              ) : (
                <>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  {scanned ? 'Re-scan' : 'Start Scan'}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Fix Results */}
        {fixResults && (
          <Card className="mb-6 border-green-200 bg-green-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Fixes Applied Successfully
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-green-700 mb-4">
                Fixed {fixResults.fixes_applied} user balance(s)
              </p>
              {fixResults.fixes?.length > 0 && (
                <div className="max-h-48 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-green-100">
                      <tr>
                        <th className="p-2 text-left">User</th>
                        <th className="p-2 text-right">Old Balance</th>
                        <th className="p-2 text-center"></th>
                        <th className="p-2 text-right">New Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fixResults.fixes.map((fix, idx) => (
                        <tr key={idx} className="border-b border-green-200">
                          <td className="p-2">
                            <p className="font-medium">{fix.name}</p>
                            <p className="text-xs text-muted-foreground">{fix.phone}</p>
                          </td>
                          <td className="p-2 text-right font-mono text-red-600">
                            {formatMilli(fix.old_balance)}
                          </td>
                          <td className="p-2 text-center">
                            <ArrowRight className="w-4 h-4 text-green-600 inline" />
                          </td>
                          <td className="p-2 text-right font-mono text-green-600">
                            {formatMilli(fix.new_balance)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Discrepancies Table */}
        {scanned && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calculator className="w-5 h-5" />
                    Balance Discrepancies
                  </CardTitle>
                  <CardDescription>
                    {discrepancies.length === 0 
                      ? 'All user balances match their transaction history'
                      : `Found ${discrepancies.length} user(s) with incorrect balances`}
                  </CardDescription>
                </div>
                {discrepancies.length > 0 && (
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      onClick={handleApplyFixes}
                      disabled={applyLoading || selectedUsers.length === 0}
                      data-testid="apply-selected-fixes-btn"
                    >
                      {applyLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <ShieldCheck className="w-4 h-4 mr-2" />
                      )}
                      Fix Selected ({selectedUsers.length})
                    </Button>
                    <Button
                      onClick={handleFixAll}
                      disabled={applyLoading}
                      className="bg-amber-600 hover:bg-amber-700"
                      data-testid="fix-all-btn"
                    >
                      {applyLoading ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Fix All ({discrepancies.length})
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {discrepancies.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-green-700">All Balances Correct</h3>
                  <p className="text-muted-foreground">
                  No discrepancies found between stored and calculated balances.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-amber-50 sticky top-0">
                      <tr>
                        <th className="p-3 text-left">
                          <Checkbox 
                            checked={selectAll}
                            onCheckedChange={handleSelectAll}
                            data-testid="select-all-checkbox"
                          />
                        </th>
                        <th className="p-3 text-left">User</th>
                        <th className="p-3 text-right">Stored Balance</th>
                        <th className="p-3 text-right">Calculated Balance</th>
                        <th className="p-3 text-right">Discrepancy</th>
                        <th className="p-3 text-left">Breakdown</th>
                      </tr>
                    </thead>
                    <tbody>
                      {discrepancies.map((item) => (
                        <tr 
                          key={item.user_id} 
                          className="border-b hover:bg-amber-50/50"
                        >
                          <td className="p-3">
                            <Checkbox 
                              checked={selectedUsers.includes(item.user_id)}
                              onCheckedChange={(checked) => handleSelectUser(item.user_id, checked)}
                            />
                          </td>
                          <td className="p-3">
                            <p className="font-medium">{item.name}</p>
                            <p className="text-xs text-muted-foreground">{item.phone}</p>
                            <p className="text-xs text-muted-foreground font-mono">{item.user_id}</p>
                          </td>
                          <td className="p-3 text-right font-mono">
                            <span className="text-red-600">{formatMilli(item.stored_balance)}</span>
                          </td>
                          <td className="p-3 text-right font-mono">
                            <span className="text-green-600">{formatMilli(item.calculated_balance)}</span>
                          </td>
                          <td className="p-3 text-right">
                            <Badge variant={item.discrepancy > 0 ? "destructive" : "default"}>
                              {item.discrepancy > 0 ? '+' : ''}{formatMilli(item.discrepancy)}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <div className="text-xs space-y-0.5">
                              <p className="text-green-600">+{formatMilli(item.breakdown?.buy || 0)} buy</p>
                              <p className="text-green-600">+{formatMilli(item.breakdown?.gift_received || 0)} gifts</p>
                              <p className="text-green-600">+{formatMilli(item.breakdown?.referral || 0)} referral</p>
                              <p className="text-green-600">+{formatMilli(item.breakdown?.adjustment_credit || 0)} adj credit</p>
                              <p className="text-orange-600">-{formatMilli(item.breakdown?.sell || 0)} sell</p>
                              <p className="text-orange-600">-{formatMilli(item.breakdown?.adjustment_debit || 0)} adj debit</p>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminDataAuditPage;
